"use client"

import { useState, useEffect } from "react"
import {
  FaGraduationCap,
  FaUsers,
  FaLightbulb,
  FaIndustry,
  FaAward,
  FaTrophy,
  FaFlask,
  FaSolarPanel,
  FaChartLine,
  FaRocket,
  FaQuoteLeft,
  FaMapMarkerAlt,
  FaStar,
  FaCog,
} from "react-icons/fa"

const StudentChapter = () => {
  const [scrollY, setScrollY] = useState(0)
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [activeEvent, setActiveEvent] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const events = [
    {
      id: "chemtalk1",
      title: "Chem Talk 1",
      subtitle: "Gas Processing & Crude Extraction",
      icon: FaFlask,
      color: "from-blue-500 to-blue-700",
      description:
        "A captivating exploration into gas processing processes, extraction of crude using seawater, and real-life design applications in the industry.",
    },
    {
      id: "chemtalk2",
      title: "Chem Talk 2",
      subtitle: "Alumni Sharing Experience",
      icon: FaUsers,
      color: "from-green-500 to-green-700",
      description:
        "An intimate session where distinguished alumni shared valuable experiences and insights, offering a glimpse into the professional journey of a chemical engineer.",
    },
    {
      id: "chemtalk3",
      title: "Chem Talk 3",
      subtitle: "Petroleum Refineries & Research",
      icon: FaIndustry,
      color: "from-purple-500 to-purple-700",
      description:
        "Led by Mr. Chiranjeevulu, who worked at Mangalore Refinery and Petrochemical Ltd. for 24 years, focusing on petroleum refineries and research aspects.",
    },
    {
      id: "chemtalk4",
      title: "Chem Talk 4",
      subtitle: "Insight into Petrochemical Industry",
      icon: FaCog,
      color: "from-orange-500 to-orange-700",
      description:
        "Featured Mr. Vignesh Sankar from Reliance Industries Limited, providing valuable insights into the petrochemical industry landscape.",
    },
  ]

  const competitions = [
    {
      title: "Trivia Titans",
      description:
        "A battle of wits where members showcased their knowledge and quick thinking in an intellectually charged competition.",
      icon: FaTrophy,
      color: "from-yellow-500 to-yellow-700",
    },
    {
      title: "Ocean Odyssey",
      description:
        "Navigating through the depths of creativity, challenging participants to explore problem-solving and innovation.",
      icon: FaRocket,
      color: "from-cyan-500 to-cyan-700",
    },
  ]

  const workshops = [
    {
      title: "Solar Photovoltaic Applications",
      description:
        "Unleashing the power of sustainable energy, delving into the intricacies of solar photovoltaic applications for a greener future.",
      icon: FaSolarPanel,
      color: "from-green-500 to-green-700",
    },
    {
      title: "Distillation Column Design",
      description:
        "Bridging theory and application using Microsoft Excel, equipping participants with hands-on skills in designing distillation columns.",
      icon: FaChartLine,
      color: "from-blue-500 to-blue-700",
    },
  ]

  const milestones = [
    { date: "February 2023", event: "Chapter Formation", icon: FaRocket },
    { date: "April 2023", event: "Executive Committee Formation", icon: FaUsers },
    { date: "May 2023", event: "BPCL Industrial Visit", icon: FaIndustry },
    { date: "October 2023", event: "Grand Inauguration Ceremony", icon: FaAward },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 dark:bg-green-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 dark:bg-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-60 h-60 bg-purple-200 dark:bg-purple-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Hero Section with Parallax */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-green-600 via-blue-600 to-purple-700"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Animated Particles */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            ></div>
          ))}
        </div>

        <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-8 animate-bounce-slow">
            <FaGraduationCap className="w-12 h-12" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up">
            Student <span className="text-green-300 animate-pulse">Chapter</span>
          </h1>
          <div className="w-24 h-1 bg-green-300 mx-auto mb-8 animate-expand"></div>
          <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-500">
            IIChE Student Chapter - Amrita Vishwa Vidyapeetham
          </p>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Introduction Section */}
        <div
          data-animate
          id="intro"
          className={`relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 mb-16 overflow-hidden transform transition-all duration-700 ${
            visibleSections.has("intro") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-green-200 to-blue-200 dark:from-green-800 dark:to-blue-800 rounded-full opacity-20"></div>

          <div className="relative flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                <FaQuoteLeft className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-6">
                Welcome to Our Chapter
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Welcome to the Indian Institute of Chemical Engineers (IIChE) Student Chapter of Amrita Vishwa
                Vidyapeetham, a growing center of chemical expertise. Our chapter is a lighthouse for people who are
                passionate about chemical engineering, tucked away in the lush hallways of higher learning. We cordially
                encourage you to join us on a journey that goes beyond the conventional bounds of education in this
                dynamic environment, where creativity and curiosity collide and innovation ignites with every contact.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                The IIChE-AVVU student chapter was formed in February 2023. The full functioning of the chapter was
                started in April 2023 by the formation of 10 executive committees with 22 members, including a
                secretary, joint secretary, and treasurer. One discovery at a time, we are collaborating to shape
                chemical engineering students' futures. Welcome to a voyage where knowledge is the only restriction and
                the possibilities are endless.
              </p>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div
          data-animate
          id="timeline"
          className={`mb-16 transform transition-all duration-700 ${
            visibleSections.has("timeline") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Our Journey
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto"></div>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-green-500 to-blue-500"></div>
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`flex items-center mb-12 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? "pr-8 text-right" : "pl-8 text-left"}`}>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <div className="text-sm text-green-600 dark:text-green-400 font-semibold mb-2">
                      {milestone.date}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{milestone.event}</h3>
                  </div>
                </div>
                <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full shadow-lg">
                  <milestone.icon className="w-6 h-6 text-white" />
                </div>
                <div className="w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Statement */}
        <div
          data-animate
          id="mission"
          className={`bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl p-10 text-white mb-16 transform transition-all duration-700 ${
            visibleSections.has("mission") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center">
            <FaLightbulb className="w-16 h-16 mx-auto mb-6 animate-pulse" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              Our chapter serves as an ode to the strength of enthusiasm, teamwork, and an unwavering quest for
              knowledge. Come join us at the nexus of innovation and education, where each student actively contributes
              to the narrative of chemical excellence that is always changing, rather than merely being a participant.
            </p>
          </div>
        </div>

        {/* Events Section */}
        <div
          data-animate
          id="events"
          className={`mb-16 transform transition-all duration-700 ${
            visibleSections.has("events") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Chem Talk Series
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {events.map((event, index) => (
              <div
                key={event.id}
                className="group bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <div className={`h-2 bg-gradient-to-r ${event.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`p-4 bg-gradient-to-r ${event.color} rounded-2xl shadow-lg`}>
                      <event.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{event.title}</h3>
                      <p className="text-green-600 dark:text-green-400 font-semibold">{event.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Competitions Section */}
        <div
          data-animate
          id="competitions"
          className={`mb-16 transform transition-all duration-700 ${
            visibleSections.has("competitions") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Competitions
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {competitions.map((competition, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`p-4 bg-gradient-to-r ${competition.color} rounded-2xl shadow-lg`}>
                    <competition.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{competition.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{competition.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Workshops Section */}
        <div
          data-animate
          id="workshops"
          className={`mb-16 transform transition-all duration-700 ${
            visibleSections.has("workshops") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Workshops
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {workshops.map((workshop, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`p-4 bg-gradient-to-r ${workshop.color} rounded-2xl shadow-lg`}>
                    <workshop.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{workshop.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{workshop.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Industrial Visit Section */}
        <div
          data-animate
          id="visit"
          className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 mb-16 transform transition-all duration-700 ${
            visibleSections.has("visit") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                <FaMapMarkerAlt className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Industrial Visit - BPCL Kochi
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Embarking on a journey of practical exposure, our members visited Bharat Petroleum Corporation Limited
                (BPCL) in Kochi on May 27, 2023. This industrial visit allowed participants to witness real-world
                applications of chemical engineering principles and understand the intricacies of a leading petroleum
                corporation.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Bridging academia and industry, Connect-Ed-Careers provided a platform for students to interact with
                professionals, gaining insights into career paths and industry expectations.
              </p>
            </div>
          </div>
        </div>

        {/* Closing Statement */}
        <div
          data-animate
          id="closing"
          className={`text-center transform transition-all duration-700 ${
            visibleSections.has("closing") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes expand {
          from { width: 0; }
          to { width: 6rem; }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
        .animate-expand { animation: expand 1s ease-out 1s forwards; width: 0; }
        .animation-delay-500 { animation-delay: 0.5s; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  )
}

export default StudentChapter
